import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Checkbox, Icon, Input,
} from '@nazka/nazka.mapframe.components';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { faUserCircle } from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-light-svg-icons';

import PasswordInput from '@App/components/PasswordInput/PasswordInput';
import { useAuth } from '@App/components/AuthProvider/auth-provider.context';
import { useLoginModal } from '@services/hooks.service';

import ForgotPassword from './ForgotPassword/ForgotPassword';
import ResetPassword from './ResetPassword/ResetPassword';

import './login-modal.styl';

function LoginModal({ type, setShowLoginModal, accountConfirmed }) {
  const navigate = useNavigate();
  const [forgotPassword, setForgotPassword] = useState(false);

  const {
    resetPassword,
    closeResetPassword,
    email: resetPasswordEmail,
    code,
  } = useLoginModal();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userWantsToBeRemembered, setUserWantsToBeRemembered] = useState(false);

  const [emailError, setEmailError] = useState();
  const [passwordError, setPasswordError] = useState();
  const [groupError, setGroupError] = useState('');
  const [firstSignIn, setFirstSignIn] = useState(false);

  const {
    logIn,
    loading,
    isLoggingIn,
    loginError,
    logOut,
  } = useAuth();

  async function onSubmit() {
    const signInResult = await logIn({ email, password, userWantsToBeRemembered });

    if (signInResult.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      setFirstSignIn({
        session: signInResult.Session,
        email,
      });
      return;
    }

    const { groups } = signInResult;
    if (type === 'jobcoach' && !groups.includes('jobcoaches')) {
      await logOut();
      setGroupError('Je bent niet geregistreerd als jobcoach.');
    } else if (type === 'werkgever' && !groups.includes('employers')) {
      await logOut();
      setGroupError('Je bent niet geregistreerd als werkgever.');
    } else {
      window.gtag?.('event', 'login_success', {
        event_category: 'Account',
        event_label: 'Successful login',
        user_properties: {
          user_type: type,
        },
      });
      if (groups && groups.includes('employers')) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    }
  }

  useEffect(() => {
    if (loginError) {
      setEmailError(true);
      setPasswordError(true);
    }
  }, [loginError]);

  if (resetPassword || firstSignIn) {
    return (
      <ResetPassword
        setShowLoginModal={setShowLoginModal}
        onClose={() => {
          setFirstSignIn(undefined);
          closeResetPassword();
        }}
        isLoggingIn={isLoggingIn}
        loading={loading}
        email={resetPasswordEmail}
        code={code}
        firstSignIn={firstSignIn}
      />
    );
  }

  if (forgotPassword) {
    return (
      <ForgotPassword
        setShowLoginModal={setShowLoginModal}
        setForgotPassword={setForgotPassword}
        isLoggingIn={isLoggingIn}
        loading={loading}
      />
    );
  }

  return (
    <div className="login-modal">
      <div
        className="login-modal__background"
        onClick={() => setShowLoginModal(false)}
      />
      <div className={`login-modal__content${loading ? ' is-loading' : ''}`}>
        <div className="login-modal__content__header">
          <div className="login-modal__content__header__title">
            <Icon icon={faUserCircle} className="login-modal__content__header__title__icon" />
            <h2 className="login-modal__content__header__title__text">
              {accountConfirmed ? 'Registratie gelukt!' : `Log in${(type === 'jobcoach' || type === 'werkgever') ? ` als ${type}` : ''}`}
            </h2>
          </div>
          <Button
            icon={faClose}
            layout="ghost"
            hover={false}
            className="login-modal__content__header__close"
            onClick={() => setShowLoginModal(false)}
          />
        </div>
        <div className="login-modal__content__form">
          <label
            className={`login-modal__content__form__label${emailError ? ' login-modal__content__form__label--error' : ''}`}
          >
            <span className="login-modal__content__form__label__text">
              E-mailadres
            </span>
            <Input
              className="login-modal__content__form__label__input"
              value={email}
              onInputChange={(v) => {
                setEmail(v?.target?.value);
                setEmailError(false);
                setGroupError('');
              }}
              rootElProps={{ onSelect: () => setEmailError(false) }}
            />
          </label>
          <label
            className={`login-modal__content__form__label${passwordError ? ' login-modal__content__form__label--error' : ''}`}
          >
            <span className="login-modal__content__form__label__text">
              Wachtwoord
            </span>
            <PasswordInput
              className="login-modal__content__form__label__input"
              value={password}
              onInputChange={(value) => {
                setPassword(value);
                setPasswordError(false);
              }}
              onSelect={() => setPasswordError(false)}
            />
          </label>
          <div className="login-modal__content__form__footer">
            <Checkbox
              checked={userWantsToBeRemembered}
              onInputChange={(v) => {
                setUserWantsToBeRemembered(v?.target?.checked);
                window.gtag?.('event', 'login-modal_remember_password_toggle', {
                  event_category: 'Account',
                  event_label: 'Login modal toggle remember password',
                  checked: v?.target?.checked,
                });
              }}
              text="Onthoud wachtwoord"
              icon={faSquare}
            />
            <Button
              className="login-modal__content__form__footer__forgot"
              layout="ghost"
              hover={false}
              onClick={() => {
                setForgotPassword(true);
                window.gtag?.('event', 'login-modal_click_forgot_password', {
                  event_category: 'Account',
                  event_label: 'Forgot password click',
                });
              }}
            >
              Wachtwoord vergeten?
            </Button>
          </div>
          {groupError && (
            <p className="login-modal__content__form__label__error">
              {groupError}
            </p>
          )}
          <Button
            className="login-modal__content__form__submit"
            onClick={() => {
              if (!email.length) setEmailError(true);
              if (!password.length) setPasswordError(true);
              if (email.length && password.length) onSubmit();
              window.gtag?.('event', 'login-modal_click_login', {
                event_category: 'Account',
                event_label: 'Login modal click login button',
                type,
              });
            }}
            isLoading={isLoggingIn}
          >
            Inloggen
          </Button>
          {type && (
            <Button
              className="login-modal__content__form__register"
              layout="ghost"
              hover={false}
              onClick={() => {
                navigate(`/${type}`);
                setShowLoginModal(false);
                window.gtag?.('event', 'login-modal_click_register', {
                  event_category: 'Account',
                  event_label: 'Login modal click register button',
                  type,
                });
              }}
            >
              Nog geen account? Registreer je nu.
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginModal;
